import { onMounted, onUnmounted } from "vue";
import { useGlobalUndoRedo } from "./useUndoRedoWithStorage";
import { useGlobalLoader } from "./useGlobalLoader";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";

export function useKeyboardShortcuts() {
  const { undo, redo, canUndo, canRedo, lastAction, nextAction } =
    useGlobalUndoRedo();
  const { showLoader, hideLoader } = useGlobalLoader();
  const toast = useToast();
  const { t } = useI18n({ useScope: "global" });

  const handleKeyDown = async (event: KeyboardEvent) => {
    // Проверяем, что не находимся в поле ввода
    const target = event.target as HTMLElement;
    if (
      target.tagName === "INPUT" ||
      target.tagName === "TEXTAREA" ||
      target.contentEditable === "true"
    ) {
      return;
    }

    // Ctrl/Cmd + Z - Отменить (работает на любой раскладке)
    if (
      (event.ctrlKey || event.metaKey) &&
      event.code === "KeyZ" &&
      !event.shiftKey
    ) {
      event.preventDefault();

      if (canUndo.value) {
        try {
          // Сохраняем описание действия перед выполнением undo
          const actionToUndo = lastAction.value;
          const actionDescription =
            actionToUndo?.description || t("keywords.shortcuts.undoDefault");
          showLoader(t("keywords.shortcuts.undoing", { action: actionDescription }));

          await undo();

          hideLoader();

          if (actionToUndo) {
            toast.info(
              t("toasts.actionUndone", { action: actionToUndo.description }),
            );
          }
        } catch (error) {
          hideLoader();
          toast.error(t("toasts.undoFailed"));
        }
      }
    }

    // Ctrl/Cmd + Y или Ctrl/Cmd + Shift + Z - Повторить (работает на любой раскладке)
    if (
      ((event.ctrlKey || event.metaKey) && event.code === "KeyY") ||
      ((event.ctrlKey || event.metaKey) &&
        event.shiftKey &&
        event.code === "KeyZ")
    ) {
      event.preventDefault();

      if (canRedo.value) {
        try {
          // Сохраняем описание действия перед выполнением redo
          const actionToRedo = nextAction.value;
          const actionDescription =
            actionToRedo?.description || t("keywords.shortcuts.redoDefault");
          showLoader(t("keywords.shortcuts.redoing", { action: actionDescription }));

          await redo();

          hideLoader();

          if (actionToRedo) {
            toast.info(
              t("toasts.actionRedone", { action: actionToRedo.description }),
            );
          }
        } catch (error) {
          hideLoader();
          toast.error(t("toasts.redoFailed"));
        }
      }
    }
  };

  // Регистрируем обработчик при вызове функции
  window.addEventListener("keydown", handleKeyDown);

  onUnmounted(() => {
    window.removeEventListener("keydown", handleKeyDown);
  });

  return {
    canUndo,
    canRedo,
    undo,
    redo,
  };
}
